import { PDFDocument } from 'pdf-lib'
import { parsePageRanges } from './validation'

/**
 * Split PDF by page ranges
 * @param pdfBytes - The PDF file as ArrayBuffer
 * @param ranges - Page ranges (e.g., "1-5, 8, 10-12")
 * @returns Array of PDF documents as Uint8Array
 */
export async function splitPdfByRanges(
  pdfBytes: ArrayBuffer,
  ranges: string
): Promise<Uint8Array[]> {
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const totalPages = pdfDoc.getPageCount()

  // Parse page ranges (1-indexed to 0-indexed)
  const pageNumbers = parsePageRanges(ranges, totalPages)

  if (pageNumbers.length === 0) {
    throw new Error('No valid pages found in the specified ranges')
  }

  // Create new PDF with selected pages
  const newPdf = await PDFDocument.create()
  const pages = await newPdf.copyPages(pdfDoc, pageNumbers.map(p => p - 1))

  pages.forEach(page => newPdf.addPage(page))

  const pdfBytesOutput = await newPdf.save()
  return [pdfBytesOutput]
}

/**
 * Split PDF into individual pages
 * @param pdfBytes - The PDF file as ArrayBuffer
 * @returns Array of PDF documents, one per page
 */
export async function splitPdfIntoPages(pdfBytes: ArrayBuffer): Promise<Uint8Array[]> {
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const totalPages = pdfDoc.getPageCount()

  const results: Uint8Array[] = []

  for (let i = 0; i < totalPages; i++) {
    const newPdf = await PDFDocument.create()
    const [page] = await newPdf.copyPages(pdfDoc, [i])
    newPdf.addPage(page)

    const pdfBytesOutput = await newPdf.save()
    results.push(pdfBytesOutput)
  }

  return results
}

/**
 * Extract specific pages and save as separate PDFs
 * @param pdfBytes - The PDF file as ArrayBuffer
 * @param pageGroups - Array of page ranges, each will become a separate PDF
 * @returns Array of PDF documents
 */
export async function extractPageGroups(
  pdfBytes: ArrayBuffer,
  pageGroups: string[]
): Promise<Uint8Array[]> {
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const totalPages = pdfDoc.getPageCount()

  const results: Uint8Array[] = []

  for (const group of pageGroups) {
    const pageNumbers = parsePageRanges(group, totalPages)

    if (pageNumbers.length === 0) continue

    const newPdf = await PDFDocument.create()
    const pages = await newPdf.copyPages(pdfDoc, pageNumbers.map(p => p - 1))

    pages.forEach(page => newPdf.addPage(page))

    const pdfBytesOutput = await newPdf.save()
    results.push(pdfBytesOutput)
  }

  return results
}

/**
 * Get PDF page count
 */
export async function getPdfPageCount(pdfBytes: ArrayBuffer): Promise<number> {
  const pdfDoc = await PDFDocument.load(pdfBytes)
  return pdfDoc.getPageCount()
}

/**
 * Read file as ArrayBuffer
 */
export async function fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}
